import type { TableColumnType } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { TableDefault } from '@/components/antd/TableDefault';

function focusRow(container: HTMLDivElement | null, index: number): void {
    container?.querySelector<HTMLElement>(`[data-item-index="${index}"]`)?.focus();
}

export function ProdutoSelectTable<T extends object>({
    columns,
    data,
    emptyText,
    initialSelectedRowKey,
    onSelect,
    onFocusChange,
    rowKey,
}: {
    columns: TableColumnType<T>[];
    data: T[];
    emptyText?: string;
    initialSelectedRowKey?: string | number;
    onSelect: (record: T) => void;
    onFocusChange?: (record: T | null) => void;
    rowKey: (record: T) => string | number;
}) {
    const containerRef = useRef<HTMLDivElement>(null);

    const initialIndex = Math.max(
        0,
        data.findIndex((record) => rowKey(record) === initialSelectedRowKey),
    );
    const [indexFocus, setIndexFocus] = useState(initialIndex);

    const focusedRecord = data[indexFocus];

    useEffect(() => {
        setIndexFocus(initialIndex);
        if (data.length > 0) {
            focusRow(containerRef.current, initialIndex);
        }
    }, [data.length, initialIndex]);

    return (
        <div ref={containerRef}>
            <TableDefault<T>
                columns={columns}
                dataSource={data}
                locale={emptyText ? { emptyText } : undefined}
                onRow={(record, index = 0) => ({
                    'data-item-index': index,
                    'data-item-selected': index === indexFocus,
                    onClick: () => onSelect(record),
                    onFocus: () => {
                        setIndexFocus(index);
                        onFocusChange?.(record);
                    },
                    onKeyDown: (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            onSelect(record);
                        }

                        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                            event.preventDefault();
                            event.stopPropagation();
                            const nextIndex = index + (event.key === 'ArrowUp' ? -1 : 1);
                            focusRow(
                                containerRef.current,
                                Math.max(0, Math.min(data.length - 1, nextIndex)),
                            );
                        }
                    },
                    tabIndex: 0,
                })}
                pagination={false}
                rowClassName={(_, index) =>
                    `cursor-pointer focus:outline-none ${index === indexFocus ? 'bg-gray-200' : ''}`
                }
                rowKey={rowKey}
                rowSelection={{
                    hideSelectAll: true,
                    onSelect: (record) => onSelect(record),
                    selectedRowKeys: focusedRecord ? [rowKey(focusedRecord)] : [],
                    type: 'checkbox',
                }}
                scroll={{ x: 600 }}
            />
        </div>
    );
}
